import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { api, ApiError } from '../../lib/api'

/**
 * Planche 01 du cahier des charges — « Vos idées, au même endroit. »
 *
 * Écarts assumés et consignés :
 *  - les boutons « Continuer avec Apple » et Google sont retirés (décision B2, validée) ;
 *  - la mention légale utilise l'encre secondaire du système au lieu du #8A8F89 de la planche,
 *    qui ne donne que 3,11:1 sur la surface alors que le cahier exige 4.5:1. Voir E10.
 */
export function AuthScreen() {
  const [email, setEmail] = useState('')
  const [sentTo, setSentTo] = useState<string | null>(null)
  const [resendIn, setResendIn] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [motDePasse, setMotDePasse] = useState(false)

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    setError(null)
    setSubmitting(true)

    try {
      const { resendAfterSeconds } = await api.requestMagicLink(email)
      setSentTo(email)
      setResendIn(resendAfterSeconds)
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "L'envoi a échoué. Réessayez.")
    } finally {
      setSubmitting(false)
    }
  }

  if (sentTo) {
    return <LinkSent email={sentTo} resendIn={resendIn} onResend={() => setSentTo(null)} />
  }

  if (motDePasse) {
    return <PasswordForm onBack={() => setMotDePasse(false)} />
  }

  return (
    <Screen>
      <div
        className="bg-accent"
        style={{ width: 56, height: 56, borderRadius: 'var(--radius-card)' }}
      />

      <h1
        className="mt-8 font-serif text-screen"
      >
        Vos idées,
        <br />
        au même endroit.
      </h1>

      <p className="mt-4 text-ink-muted" style={{ fontSize: 17, lineHeight: 1.5 }}>
        Entrez votre e-mail. Nous envoyons un lien de connexion, sans mot de passe.
      </p>

      <form className="mt-8" onSubmit={handleSubmit} noValidate>
        <label className="sr-only" htmlFor="email">
          Adresse e-mail
        </label>
        <input
          id="email"
          name="email"
          type="email"
          inputMode="email"
          autoComplete="email"
          required
          placeholder="lea@exemple.fr"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          aria-invalid={error !== null}
          aria-describedby={error ? 'email-error' : undefined}
          className="w-full border-[1.5px] bg-card px-4 outline-none"
          style={{
            height: 56,
            borderRadius: 'var(--radius-field)',
            borderColor: error ? 'var(--color-danger)' : 'var(--color-accent)',
            fontSize: 17,
          }}
        />

        {error ? (
          <p id="email-error" className="mt-3 text-meta" style={{ color: 'var(--color-danger)' }}>
            {error}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={submitting}
          className="mt-4 w-full bg-accent text-card-title font-semibold text-card disabled:opacity-60"
          style={{ height: 56, borderRadius: 'var(--radius-button)' }}
        >
          {submitting ? 'Envoi…' : 'Recevoir mon lien'}
        </button>
      </form>

      {/* Voie secondaire, réservée aux comptes qui ont un mot de passe — l'exploitation.
          Écart au cahier assumé : sans elle, entrer depuis un appareil neuf suppose d'aller
          chercher le lien dans les journaux du serveur (point ouvert P2). */}
      <button
        type="button"
        onClick={() => setMotDePasse(true)}
        className="mt-6 text-meta font-semibold text-accent"
      >
        Se connecter avec un mot de passe
      </button>

      <p className="mt-6 text-meta text-ink-muted" style={{ lineHeight: 1.5 }}>
        En continuant, vous acceptez les conditions d'utilisation et la politique de
        confidentialité.
      </p>
    </Screen>
  )
}

/**
 * Écran d'attente exigé par la note de la planche 01 :
 * « Lien envoyé à lea@exemple.fr · Renvoyer dans 30 s ».
 */
function LinkSent({
  email,
  resendIn,
  onResend,
}: {
  email: string
  resendIn: number
  onResend: () => void
}) {
  const remaining = useCountdown(resendIn)

  return (
    <Screen>
      <h1
        className="font-serif text-screen"
      >
        Lien envoyé.
      </h1>

      <p className="mt-4 text-ink-muted" style={{ fontSize: 17, lineHeight: 1.5 }}>
        Ouvrez le message envoyé à <span className="text-ink">{email}</span> pour vous connecter.
      </p>

      <button
        type="button"
        onClick={onResend}
        disabled={remaining > 0}
        className="mt-8 w-full border-[1.5px] border-border bg-card text-card-title font-semibold disabled:text-ink-muted"
        style={{ height: 56, borderRadius: 'var(--radius-button)' }}
      >
        {remaining > 0 ? `Renvoyer dans ${remaining} s` : 'Renvoyer le lien'}
      </button>
    </Screen>
  )
}


/**
 * Voie du mot de passe, au sein de l'écran de connexion.
 *
 * Les comptes ordinaires n'en ont pas : l'API répond identiquement à un identifiant inconnu, un
 * compte sans mot de passe et un mot de passe faux. Ce n'est donc pas une seconde porte vers
 * l'application, c'est la même porte avec une autre clé.
 */
function PasswordForm({ onBack }: { onBack: () => void }) {
  const [login, setLogin] = useState('')
  const [password, setPassword] = useState('')
  const [erreur, setErreur] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const connecter = useMutation({
    mutationFn: () => api.passwordLogin(login.trim(), password),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['me'] }),
    onError: (cause: unknown) =>
      setErreur(cause instanceof ApiError ? cause.message : 'La connexion a échoué.'),
  })

  return (
    <Screen>
      <h1 className="font-serif text-screen">Connexion</h1>

      <form
        className="mt-8"
        onSubmit={(event) => {
          event.preventDefault()
          setErreur(null)
          connecter.mutate()
        }}
      >
        <label className="block text-label font-semibold uppercase text-ink-muted" htmlFor="identifiant">
          Identifiant ou e-mail
        </label>
        <input
          id="identifiant"
          autoComplete="username"
          autoCapitalize="none"
          value={login}
          onChange={(event) => {
            setLogin(event.target.value)
            setErreur(null)
          }}
          className="mt-2 w-full rounded-field border-[1.5px] border-accent bg-card px-4 outline-none"
          style={{ height: 56, fontSize: 17 }}
        />

        <label className="mt-4 block text-label font-semibold uppercase text-ink-muted" htmlFor="mdp">
          Mot de passe
        </label>
        <input
          id="mdp"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => {
            setPassword(event.target.value)
            setErreur(null)
          }}
          className="mt-2 w-full rounded-field border-[1.5px] border-accent bg-card px-4 outline-none"
          style={{ height: 56, fontSize: 17 }}
        />

        {erreur ? (
          <p className="mt-3 text-meta" style={{ color: 'var(--color-danger)' }}>
            {erreur}
          </p>
        ) : null}

        <button
          type="submit"
          disabled={login.length === 0 || password.length === 0 || connecter.isPending}
          className="mt-6 w-full rounded-button bg-accent text-card-title font-semibold text-card disabled:opacity-60"
          style={{ height: 56 }}
        >
          {connecter.isPending ? 'Connexion…' : 'Se connecter'}
        </button>
      </form>

      <button type="button" onClick={onBack} className="mt-6 text-meta font-semibold text-accent">
        Revenir au lien par e-mail
      </button>
    </Screen>
  )
}

/** Décompte du « Renvoyer dans 30 s » de la planche 01. */
function useCountdown(from: number): number {
  const [remaining, setRemaining] = useState(from)

  useEffect(() => {
    setRemaining(from)
  }, [from])

  useEffect(() => {
    if (remaining <= 0) {
      return
    }

    const timer = setTimeout(() => setRemaining((value) => value - 1), 1000)
    return () => clearTimeout(timer)
  }, [remaining])

  return remaining
}

function Screen({ children }: { children: ReactNode }) {
  return (
    <main
      className="mx-auto flex min-h-dvh flex-col justify-center bg-surface"
      style={{ maxWidth: 'var(--layout-column)', paddingInline: 'var(--layout-gutter)' }}
    >
      {children}
    </main>
  )
}
